/**
 * Full-screen photo viewer opened from the monastery hero — the mosaic tiles
 * and the "Show all photos" button both land here. Arrow keys / on-screen
 * arrows / swipe to move, Escape or the scrim to close. Per-photo attribution
 * stays visible (a licence condition — see PhotoCredit).
 */
import { useEffect, useRef } from 'react';
import Icon from './Icon';
import PhotoCredit from './PhotoCredit';

export default function PhotoLightbox({ photos, index, setIndex, onClose, label }) {
  const count = photos.length;
  const touchStart = useRef(null);
  const go = (delta) => setIndex((current) => (current + delta + count) % count);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') go(-1);
      else if (event.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const onTouchStart = (event) => {
    const point = event.touches[0];
    touchStart.current = point ? { x: point.clientX, y: point.clientY } : null;
  };
  const onTouchEnd = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const end = event.changedTouches[0];
    if (!end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };

  const photo = photos[index];

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={label}>
      <div className="lightbox__bar">
        <span className="lightbox__count">{index + 1} / {count}</span>
        <button type="button" className="lightbox__icon-btn" onClick={onClose} aria-label="Close">
          <Icon name="close" size={20} />
        </button>
      </div>

      <div
        className="lightbox__stage"
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={photo.source}
          alt={label}
          className="lightbox__img"
          onClick={(event) => event.stopPropagation()}
        />
        {count > 1 && (
          <>
            <button
              type="button"
              className="lightbox__icon-btn lightbox__nav lightbox__nav--prev"
              onClick={(event) => { event.stopPropagation(); go(-1); }}
              aria-label="Previous photograph"
            >
              <Icon name="chevronLeft" size={24} />
            </button>
            <button
              type="button"
              className="lightbox__icon-btn lightbox__nav lightbox__nav--next"
              onClick={(event) => { event.stopPropagation(); go(1); }}
              aria-label="Next photograph"
            >
              <Icon name="chevronRight" size={24} />
            </button>
          </>
        )}
      </div>

      {photo.credit && (
        <div className="lightbox__credit">
          <PhotoCredit photo={photo} variant="on-photo" />
        </div>
      )}
    </div>
  );
}
